import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'

import noNotifications from 'assets/images/no-notifications.png'
import * as spriteIcons from 'assets/svg/sprite'
import LoadingDots from 'components/UI/LoadingDots/LoadingDots'
import SortModal from 'components/UI/Modal/Sort/SortModal'
import { useModalContext } from 'context/modalContext'
import { usePageTitle } from 'context/pageTitleContext'
import NotificationCard from 'features/NotificationCard/NotificationCard'
import { useFilterQuery, useUserActivityFeedInfinite } from 'helpers/hooks'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getFanDetails } from 'services/endpoints/profile'

import style from './_user_activity.module.scss'

interface IUserActivityTitleProps {
    name?: string
    username?: string
    avatar?: string
}

interface IUserActivityProps {
    userInfo?: IUserActivityTitleProps
}

export const UserActivity = ({ userInfo }: IUserActivityProps) => {
    const { setTitle } = usePageTitle()
    const { t } = useTranslation()
    const { id } = useParams<{ id: string }>()
    const modalData = useModalContext()

    const filter = useFilterQuery()
    const type = filter.get('type')

    const userId = id

    const [filterSelected, setFilterSelected] = useState<string>('all')

    const { ref: lastNotificationRef, inView } = useInView({
        threshold: 0.1
    })

    const reqFilter = useMemo(
        () => ({
            type: filterSelected,
            userId
        }),
        [filterSelected, userId]
    )

    const {
        data,
        error,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage
    } = useUserActivityFeedInfinite(reqFilter)

    const notifications = useMemo(() => {
        if (!data?.pages?.length) return []

        return data.pages.flatMap(page => page?.page?.data?.data || [])
    }, [data])

    const notificationsFeedStatus = useMemo(() => {
        if (isFetching && !data) return ''
        return notifications.length ? 'hasNotifications' : 'empty'
    }, [notifications, isFetching, data])

    useEffect(() => {
        if (type) {
            setFilterSelected(type)
        } else {
            setFilterSelected('all')
        }
    }, [type])

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
        }
    }, [fetchNextPage, hasNextPage, inView, isFetchingNextPage])

    const firstNotificationSender = useMemo(() => {
        return notifications?.[0]?.data?.sender || null
    }, [notifications])

    const titleAvatarFromProps = userInfo?.avatar
    const titleNameFromProps = userInfo?.name ?? userInfo?.username

    const titleAvatarFromNotification = firstNotificationSender?.avatar
    const titleNameFromNotification =
        firstNotificationSender?.name || firstNotificationSender?.username

    const notificationsResolved = !!data && !isFetching

    const shouldFetchFanDetails =
        !!userId &&
        notificationsResolved &&
        !titleAvatarFromProps &&
        !titleNameFromProps &&
        !titleAvatarFromNotification &&
        !titleNameFromNotification

    const { data: fanDetails } = useQuery(
    ['fanDetails', userId],
    async () => {
        if (!userId) {
        throw new Error('Missing userId')
        }

        return await getFanDetails(userId)
    },
    {
        enabled: shouldFetchFanDetails && !!userId
    }
    )
    
    const resolvedTitleAvatar =
        titleAvatarFromProps ??
        titleAvatarFromNotification ??
        fanDetails?.avatar?.url ??
        fanDetails?.avatar ??
        ''

    const resolvedTitleName =
        titleNameFromProps ??
        titleNameFromNotification ??
        fanDetails?.display_name ??
        fanDetails?.name ??
        fanDetails?.username ??
        ''

    const titleContent = useMemo(
        () => (
            <div className={style.layout_title}>
                <div className={`${style.avatar} ${style.avatar_small}`}>
                    {resolvedTitleAvatar ? (
                        <img src={resolvedTitleAvatar} alt='avatar' />
                    ) : null}
                </div>
                {resolvedTitleName}
            </div>
        ),
        [resolvedTitleAvatar, resolvedTitleName]
    )

    useEffect(() => {
        setTitle(titleContent)
    }, [setTitle, titleContent])

    const fitlerElements = {
        first_section: [
            {
                value: 'all',
                name: t('All')
            },
            {
                value: 'interactions',
                name: t('interactions')
            },
            {
                value: 'likes',
                name: t('likes')
            },
            {
                value: 'subscriptions',
                name: t('subscriptions')
            },
            {
                value: 'tips',
                name: t('tips')
            }
        ]
    }

    const selectedFilterName =
        fitlerElements.first_section.find(
            filterOption => filterOption.value === filterSelected
        )?.name || t('All')

    const activityTitle =
        filterSelected === 'all'
            ? t('activity')
            : selectedFilterName

    useEffect(() => {
        if (error) {
            console.error('pages > UserActivity.tsx', error)
        }
    }, [error])

    return (
        <WithHeaderSection
            customClass={style.header_section}
            headerSection={
                <div className={style.title}>
                    {activityTitle}
                    <div
                        className={style.filter_button}
                        onClick={() => {
                            modalData.addModal(
                                t('filter'),
                                <SortModal
                                    elements={fitlerElements}
                                    applyFn={(val: any) => {
                                        setFilterSelected(val)
                                    }}
                                    hasResetBtn={true}
                                    resetFn={() => {
                                        modalData.clearModal()
                                        setFilterSelected('all')
                                    }}
                                    sortingProps={{
                                        selectedOrder: '',
                                        selectedSort: filterSelected
                                    }}
                                />
                            )
                        }}
                    >
                        <spriteIcons.IconFilterOutline />
                    </div>
                </div>
            }
        >
            {isFetching && !isFetchingNextPage && (
                <div className={style.loader_container}>
                    <BeatLoader size={8} />
                </div>
            )}

            {notificationsFeedStatus === 'hasNotifications' && (
                <div className='notificationsList'>
                    {notifications.map((notification: any, index: number) => {
                        const card = (
                            <NotificationCard
                                data={{
                                    is_online: notification?.is_online,
                                    is_liked: notification?.is_liked,
                                    ...notification.data
                                }}
                                type={notification.data.notification_type}
                                time={notification.created_at}
                            />
                        )

                        if (notifications.length - 1 === index) {
                            return (
                                <div
                                    key={notification.id}
                                    ref={lastNotificationRef}
                                >
                                    {card}
                                </div>
                            )
                        }

                        return <div key={notification.id}>{card}</div>
                    })}

                    <LoadingDots visible={isFetchingNextPage} />
                </div>
            )}

            {notificationsFeedStatus === 'empty' && (
                <div className='notificationsList__empty'>
                    <img src={noNotifications} alt='noNotifications' />
                    <div className='notificationsList__empty__text'>
                        <div className='notificationsList__empty__text--title'>
                            {t('noActivity')}
                        </div>
                        <div className='notificationsList__empty__text--sub'>
                            {t('noActivity')}
                        </div>
                    </div>
                </div>
            )}
        </WithHeaderSection>
    )
}